import { FC } from 'react';
import { Dropdown, InlineLoading, TextInput, Tile } from '@carbon/react';
import { Information } from '@carbon/react/icons';
import { TooltipIcon } from 'components/tooltip-icon/tooltip-icon';
import { formatCurrency } from './cost-utils';
import { HOLD_FLAG_OPTIONS } from './identification-utils';
import styles from './cost.module.scss';

interface HoldFlagOption {
  label: string;
  value: string;
}

interface Props {
  wellId: number;
  grandTotal: number;
  drillingTotal: number;
  completionTotal: number;
  facilitiesTotal: number;
  contingenciesTotal: number;
  otherTotal: number;
  netAfeCost: number | null;
  canModify: boolean;
  /** Live Working Interest % input value. */
  wiInput: string;
  wiSaving: boolean;
  onWiChange: (value: string) => void;
  onWiCommit: () => void;
  selectedHeldFlagOption: HoldFlagOption;
  heldFlagSaving: boolean;
  onHeldFlagChange: (option: HoldFlagOption | null) => void;
}

export const StructureCostsSummary: FC<Props> = ({
  wellId,
  grandTotal,
  drillingTotal,
  completionTotal,
  facilitiesTotal,
  contingenciesTotal,
  otherTotal,
  netAfeCost,
  canModify,
  wiInput,
  wiSaving,
  onWiChange,
  onWiCommit,
  selectedHeldFlagOption,
  heldFlagSaving,
  onHeldFlagChange,
}) => {
  return (
    <>
      <div className={styles.summaryTiles}>
        <Tile className={styles.summaryTile}>
          <p className={styles.summaryValue}>{formatCurrency(grandTotal)}</p>
          <p className={styles.summaryLabel}>Grand Total</p>
        </Tile>
        <Tile className={styles.summaryTile}>
          <p className={styles.summaryValue}>{formatCurrency(drillingTotal)}</p>
          <p className={styles.summaryLabel}>Drilling</p>
        </Tile>
        <Tile className={styles.summaryTile}>
          <p className={styles.summaryValue}>
            {formatCurrency(completionTotal)}
          </p>
          <p className={styles.summaryLabel}>Completion</p>
        </Tile>
        <Tile className={styles.summaryTile}>
          <p className={styles.summaryValue}>
            {formatCurrency(facilitiesTotal)}
          </p>
          <p className={styles.summaryLabel}>Facilities</p>
        </Tile>
        {contingenciesTotal > 0 && (
          <Tile className={styles.summaryTile}>
            <p className={styles.summaryValue}>
              {formatCurrency(contingenciesTotal)}
            </p>
            <p className={styles.summaryLabel}>Contingencies &amp; Overhead</p>
          </Tile>
        )}
        {otherTotal > 0 && (
          <Tile className={styles.summaryTile}>
            <p className={styles.summaryValue}>{formatCurrency(otherTotal)}</p>
            <p className={styles.summaryLabel}>Other</p>
          </Tile>
        )}
      </div>

      <div className={styles.summaryInputs}>
        <div className={styles.summaryInput}>
          <TextInput
            id={`cost-wi-${wellId}`}
            labelText={
              <span className={styles.inputLabelWithTooltip}>
                Working Interest %
                <TooltipIcon
                  definition="The cost-bearing ownership percentage in this well. Enter a value to compute Net AFE Cost as Grand Total × Working Interest."
                  direction="top"
                >
                  <Information size={16} />
                </TooltipIcon>
              </span>
            }
            size="md"
            value={wiInput}
            disabled={wiSaving}
            readOnly={!canModify}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onWiChange(e.target.value)
            }
            onBlur={() => {
              if (canModify) onWiCommit();
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
          />
        </div>
        <div className={styles.summaryInput}>
          <TextInput
            id={`cost-net-afe-${wellId}`}
            labelText={
              <span className={styles.inputLabelWithTooltip}>
                Net AFE Cost
                <TooltipIcon
                  definition="Net AFE Cost = Grand Total × Working Interest"
                  direction="top"
                >
                  <Information size={16} />
                </TooltipIcon>
              </span>
            }
            size="md"
            value={netAfeCost === null ? '—' : formatCurrency(netAfeCost)}
            readOnly
          />
        </div>
        <div className={styles.summaryInput}>
          <Dropdown
            id={`cost-held-flag-${wellId}`}
            titleText="Hold Flag"
            label="Select…"
            size="md"
            items={HOLD_FLAG_OPTIONS}
            selectedItem={selectedHeldFlagOption}
            itemToString={(i: HoldFlagOption | null) => {
              return i ? i.label : '';
            }}
            disabled={!canModify || heldFlagSaving}
            onChange={({
              selectedItem,
            }: {
              selectedItem: HoldFlagOption | null;
            }) => onHeldFlagChange(selectedItem)}
          />
          {heldFlagSaving && (
            <InlineLoading
              className={styles.heldFlagLoader}
              description="Saving…"
              aria-label="Saving held flag"
            />
          )}
        </div>
      </div>
    </>
  );
};
